<script setup lang="ts">

import * as d3 from 'd3';
import { rgb } from 'd3-color';
import { ref, watch} from 'vue';

import { ColoredSeries, TimeSeriesS } from '../fin/history';
import MoneyTimePlotBase from './MoneyTimePlotBase.vue';

interface Props {
  timeseriess: TimeSeriesS<number>;
  birthYear: number;
  retirementYear: number;
  maxYear: number;
}

// "Reactive Props Destructure" to initialise props:
const {
  timeseriess = new TimeSeriesS<number>([]),
  birthYear = 0,
  retirementYear = 0,
} = defineProps<Props>();

const coloredSeriess = ref(getColoredSeries());

watch( ()=>timeseriess, (_new, _old ) => {
  coloredSeriess.value = getColoredSeries();
});

function getColoredSeries(): Array<ColoredSeries<number>> {
  if (timeseriess.data.length == 0) {
    return [];
  }

  function getColour(t: number) {
    t = t/15;
    const pi_1_3 = Math.PI / 3;
    const pi_2_3 = Math.PI * 2 / 3;
    const c = rgb('black');
    let x;
    t = (0.5 - t) * Math.PI;
    c.r = 255 * (x = Math.sin(t)) * x;
    c.g = 255 * (x = Math.sin(t + pi_1_3)) * x;
    c.b = 255 * (x = Math.sin(t + pi_2_3)) * x;
    return c + "";
  }

  const names = timeseriess.data.map(ts => ts.name).sort();

  const cseriess: Array<ColoredSeries<number>> = Array
    .from(names.entries())
    .map(
      ([index, name]) => new ColoredSeries<number>(
        name,
        timeseriess.data.filter(ts => ts.name == name)[0].data,
        getColour(index),
      )
    );

  return cseriess;
}

function drawLines(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
  x: (x:number)=>number,
  y: (y:number)=>number,
): void {
  for (const cs of coloredSeriess.value) {
    svg.append('g')
       .classed('fin-series', true)
       .attr('fill', cs.color)
       .selectAll()
       .data(cs.data)
       .join('circle')
       .attr('r', _d => 4.0)
       .attr('cx', d => x(d.year))
       .attr('cy', d => y(d.value))
       .attr('amount', d => Math.round(d.value).toLocaleString())
       .attr('year', d => d.year)
       .append('title')
       .text(
         d => cs.name + ' '
                      + d.year
                      + ' £'
                      + Math.round(d.value).toLocaleString());

    const points: [number, number][]
      = cs.data.map(
        ya => [ya.year, ya.value]
      );

    const line = d3.line()
                   .x(d => x(d[0]))
                   .y(d => y(d[1]));

    // Join the dots.
    svg.append('path')
       .attr('class', 'fin-series')
       .attr('visibility', 'visible')
       .attr('fill', 'none')
       .attr('stroke', cs.color)
       .attr('stroke-width', 2)
       .attr('d', line(points))
       .append('title')
       .text(cs.name);
  }
}

</script>

<template>
  <MoneyTimePlotBase
    class="fin-plot output-card"
    title="Flows"
    :birthYear="birthYear"
    :maxYear="maxYear"
    :retirementYear="retirementYear"
    :seriess="coloredSeriess"
    :seriesRenderer="drawLines"
  />
</template>

<style scoped>
</style>
